import { defineMessages, useIntl } from 'react-intl'
import { PreferredPartnerCard } from '../../../../../../../shared/HomebotNetwork/PreferredPartnerCard'
import { useVerificationPrequalStep } from '../../../../../../../../hooks/homebotNetwork/verificationFlow/useVerificationPrequalStep'
import { Box, Button, Stack, Text, useToast } from '@homebotapp/hb-react-component-catalog'
import { formatStreetAddress } from '../../../../../../../../helpers/formatListingAddress'
import { selectAddress } from '../../../../../../../../store/selectors/home'
import { useSelector } from 'react-redux'
import { formatCustomerProfileType } from '../../../../../../../../helpers/formatCustomerProfileType'
import { Listing } from '../../../../../../../../api/gqlaxy/gql/generated/graphql'

const MSG = defineMessages({
  add: {
    id: 'VerificationFlow.UnverifiedOptions.add',
    defaultMessage: 'Yes, add'
  },
  dontWantToWorkWithh: {
    id: 'VerificationFlow.UnverifiedOptions.dontWantToWorkWithh',
    defaultMessage: 'Don\'t want to work with {partnerName}? You can remove them using the "Your team" list.'
  },
  error: {
    id: 'VerificationFlow.UnverifiedOptions.error',
    defaultMessage: 'An error occurred while verifying the partner.'
  },
  heading: {
    id: 'VerificationFlow.UnverifiedOptions.heading',
    defaultMessage: 'We found your {customerType} from {streetAddress}. Would you like to add them to the conversation?'
  },
  notAtThisTime: {
    id: 'VerificationFlow.UnverifiedOptions.notAtThisTime',
    defaultMessage: 'Not at this time'
  },
  readyForPrequal: {
    id: 'VerificationFlow.UnverifiedOptions.readyForPrequal',
    defaultMessage: 'Ready to request a pre-qual?'
  },
  remove: {
    id: 'VerificationFlow.UnverifiedOptions.remove',
    defaultMessage: 'No, remove'
  },
  removePartner: {
    id: 'VerificationFlow.UnverifiedOptions.removePartner',
    defaultMessage: 'remove'
  },
  scheduleTourConfirmation: {
    id: 'ScheduleTour.confirmation.header',
    defaultMessage: 'Tour request sent!'
  },
  subTextRea: {
    id: 'VerificationFlow.UnverifiedOptions.subTextRea',
    defaultMessage:
      "If so, we'll keep them on your team, and they'll be kept in the loop with real estate related questions."
  },
  subTextLo: {
    id: 'VerificationFlow.UnverifiedOptions.subTextLo',
    defaultMessage: "We'll keep them on your team to help with finance questions."
  },
  terms: {
    id: 'VerificationFlow.UnverifiedOptions.terms',
    defaultMessage:
      "By clicking 'Yes, Add' you authorize HB Real Estate Services, Inc. and Homebot, Inc. to share your contact information (name and email address) and any other information you include in your question with the agent identified above. You agree that the agent may contact you about your inquiry."
  },
  willFollowUp: {
    id: 'ScheduleTour.confirmation.willFollowUp',
    defaultMessage:
      '{customerFirstName} will follow up to finalize a date and time. If you have any questions, please contact them.'
  },
  prequalSuccess: {
    id: 'VerificationPrequalView.prequalSuccess',
    defaultMessage: 'Pre-qualification request sent!'
  }
})
/**
 * VerificationPrequalView specific to the Schedule Tour flow
 *
 * Specific UI for the prequal flow with loan officers
 */
export const VerificationPrequalView = ({ listing }: { listing?: Listing }) => {
  const intl = useIntl()
  const { onSkip, verifyAndSendPrequal, isLoading, membership } = useVerificationPrequalStep(listing)
  const toast = useToast()
  const streetAddress = useSelector(selectAddress)

  const handleVerifyAndSendPrequal = async () => {
    try {
      await verifyAndSendPrequal()
      toast({
        description: intl.formatMessage(MSG.prequalSuccess),
        status: 'success'
      })
    } catch (error) {
      toast({
        description: intl.formatMessage(MSG.error),
        status: 'error'
      })
    }
  }

  return (
    <>
      <Box data-qa='verification-prequal-view' data-testid='verification-prequal-view'>
        <Box p='6' borderBottomStyle='solid' borderBottomColor='neutral.300' borderBottomWidth='1px'>
          <Text textAlign='center' fontWeight='bold'>
            ✅ {intl.formatMessage(MSG.scheduleTourConfirmation)}
          </Text>
        </Box>
        <Text size='sm' py='6'>
          {intl.formatMessage(MSG.willFollowUp, {
            customerFirstName: membership.customerProfile.firstName
          })}
        </Text>

        <PreferredPartnerCard partner={membership.customerProfile} corporateProfile={membership.corporateProfile} />
        <Text fontWeight='semibold' color='neutral.900' mt={8}>
          {intl.formatMessage(MSG.heading, {
            streetAddress: formatStreetAddress(streetAddress),
            customerType: formatCustomerProfileType('LoanOfficer')
          })}
        </Text>
        <Text size='sm' color='neutral.800' mb='7'>
          {intl.formatMessage(MSG.subTextLo)}
        </Text>

        <Text size='lg' fontWeight='700' color='neutral.900' mb='4'>
          {intl.formatMessage(MSG.readyForPrequal)}
        </Text>

        <Stack direction='row' gap={2} justifyContent='flex-end' mt='6'>
          <Button
            variant='outline'
            colorScheme='primary'
            onClick={onSkip}
            isDisabled={isLoading}
            flex={1}
            tracking={{
              guid: 'vH5KPZ3Yqg2jVhLXmzTUHA',
              ui_context: 'schedule_tour_verification_flow_verification_prequal_view',
              descriptor: MSG.notAtThisTime.defaultMessage
            }}
          >
            {intl.formatMessage(MSG.notAtThisTime)}
          </Button>
          <Button
            isLoading={isLoading}
            onClick={handleVerifyAndSendPrequal}
            flex={1}
            colorScheme='primary'
            tracking={{
              guid: '3mUmNvFrn364f7hncz5t4g',
              ui_context: 'schedule_tour_verification_flow_verification_prequal_view',
              descriptor: MSG.add.defaultMessage
            }}
          >
            {intl.formatMessage(MSG.add)}
          </Button>
        </Stack>

        <Text size='sm' color='neutral.700' mt={4}>
          {intl.formatMessage(MSG.dontWantToWorkWithh, {
            partnerName: `${membership.customerProfile.firstName} ${membership.customerProfile.lastName}`
          })}
        </Text>
      </Box>
      <Box bg='neutral.200' borderTop='1px solid' borderColor='neutral.300' mx='-6' mt='6' mb='-6' p='6'>
        <Text size='3xs' color='neutral.600'>
          {intl.formatMessage(MSG.terms)}
        </Text>
      </Box>
    </>
  )
}
